import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { FileSignature, Loader2, Shield, X } from "lucide-react";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Checkbox } from "./ui/checkbox";
import { useAuth, formatApiError } from "../context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AgreementModal() {
  const {
    agreementModalOpen,
    setAgreementModalOpen,
    signAgreement,
    postAuthRedirect,
    setPostAuthRedirect,
    user,
  } = useAuth();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [agreementText, setAgreementText] = useState("");
  const [version, setVersion] = useState("");
  const [fullName, setFullName] = useState("");
  const [acks, setAcks] = useState({
    not_medical_advice: false,
    assume_risk: false,
    release_liability: false,
    esign: false,
  });

  useEffect(() => {
    if (!agreementModalOpen) return;
    axios
      .get(`${API}/auth/agreement-text`)
      .then(({ data }) => {
        setAgreementText(data.text);
        setVersion(data.version);
      })
      .catch(() => toast.error("Failed to load agreement. Please retry."));
  }, [agreementModalOpen]);

  if (!agreementModalOpen) return null;

  const allAck = Object.values(acks).every(Boolean);
  const canSubmit = allAck && fullName.trim().length >= 2;

  const submit = async (e) => {
    e?.preventDefault();
    if (!canSubmit) return;
    setLoading(true);
    try {
      const { signature_sha256 } = await signAgreement({
        full_name: fullName.trim(),
        accept_not_medical_advice: acks.not_medical_advice,
        accept_assume_risk: acks.assume_risk,
        accept_release_liability: acks.release_liability,
        accept_esign: acks.esign,
      });
      toast.success("Agreement signed", {
        description: `Signature: ${signature_sha256.slice(0, 16)}…  PDF receipt emailed.`,
      });
      setAgreementModalOpen(false);
      if (postAuthRedirect) {
        navigate(postAuthRedirect);
        setPostAuthRedirect(null);
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[110] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
      data-testid="agreement-modal"
      role="dialog"
      aria-modal="true"
    >
      <div className="w-full max-w-2xl bg-white rounded-3xl shadow-2xl border border-gray-100 flex flex-col max-h-[92vh]">
        {/* Header */}
        <div className="relative p-6 border-b border-gray-100">
          <button
            onClick={() => setAgreementModalOpen(false)}
            className="absolute top-4 right-4 p-2 rounded-full hover:bg-gray-100"
            aria-label="Close"
            data-testid="agreement-close-btn"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-amber-500 to-rose-500 flex items-center justify-center shadow-md">
              <FileSignature className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="font-playfair text-2xl font-bold text-gray-900">
                User Liability Agreement
              </h2>
              <p className="text-xs text-gray-500">
                Required before entry · Version {version || "…"} · Signed with SHA-256
              </p>
            </div>
          </div>
        </div>

        {/* Agreement text (scrollable) */}
        <div
          className="flex-1 overflow-y-auto p-6 text-sm text-gray-700 whitespace-pre-wrap font-mono leading-relaxed bg-gray-50/50"
          data-testid="agreement-text"
        >
          {agreementText || "Loading agreement…"}
        </div>

        {/* Form */}
        <form onSubmit={submit} className="p-6 border-t border-gray-100 space-y-4 bg-white">
          <div className="grid gap-3">
            <label className="flex items-start gap-3 p-3 rounded-lg border border-gray-200 hover:bg-gray-50 cursor-pointer">
              <Checkbox
                checked={acks.not_medical_advice}
                onCheckedChange={(v) => setAcks({ ...acks, not_medical_advice: !!v })}
                data-testid="ack-medical"
                className="mt-0.5"
              />
              <span className="text-sm text-gray-700">
                I understand this app is educational and <b>not medical advice</b>.
              </span>
            </label>
            <label className="flex items-start gap-3 p-3 rounded-lg border border-gray-200 hover:bg-gray-50 cursor-pointer">
              <Checkbox
                checked={acks.assume_risk}
                onCheckedChange={(v) => setAcks({ ...acks, assume_risk: !!v })}
                data-testid="ack-risk"
                className="mt-0.5"
              />
              <span className="text-sm text-gray-700">
                I voluntarily <b>assume all risks</b> of using herbs, including interactions & allergies.
              </span>
            </label>
            <label className="flex items-start gap-3 p-3 rounded-lg border border-gray-200 hover:bg-gray-50 cursor-pointer">
              <Checkbox
                checked={acks.release_liability}
                onCheckedChange={(v) => setAcks({ ...acks, release_liability: !!v })}
                data-testid="ack-release"
                className="mt-0.5"
              />
              <span className="text-sm text-gray-700">
                I <b>release</b> Elite Herbalist and its creators from any liability for harm,
                injury, illness, disability, or death.
              </span>
            </label>
            <label className="flex items-start gap-3 p-3 rounded-lg border border-gray-200 hover:bg-gray-50 cursor-pointer">
              <Checkbox
                checked={acks.esign}
                onCheckedChange={(v) => setAcks({ ...acks, esign: !!v })}
                data-testid="ack-esign"
                className="mt-0.5"
              />
              <span className="text-sm text-gray-700">
                I agree that typing my name below is a <b>legally binding electronic signature</b>.
              </span>
            </label>
          </div>

          <div>
            <label className="text-sm font-medium text-gray-700 mb-2 block">
              Type your full legal name
            </label>
            <Input
              data-testid="agreement-name-input"
              type="text"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              placeholder="Jane Doe"
              className="h-12 text-base"
              required
            />
            <p className="text-xs text-gray-500 mt-1.5">
              Signing as <b>{user?.email}</b> · Timestamp &amp; IP will be recorded.
            </p>
          </div>

          <Button
            type="submit"
            disabled={!canSubmit || loading}
            data-testid="agreement-sign-btn"
            className="w-full h-12 rounded-full bg-gradient-to-r from-amber-600 to-rose-600 text-white font-semibold text-base shadow-lg"
          >
            {loading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <span className="flex items-center gap-2">
                <Shield className="w-4 h-4" /> Sign &amp; Enter App
              </span>
            )}
          </Button>
        </form>
      </div>
    </div>
  );
}
